import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from '@perk-ui/core'
import { useFormik } from 'formik'
import { Check, LogOut, Plus, Search } from 'react-feather'
import { useQueryClient } from 'react-query'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
import AnimatedRedirect from '../../../features/animation/AnimatedRedirect'
import { useAuth } from '../../../features/auth/AuthContext'
import QueryKeys from '../../../features/query-hooks/QueryKeys'
import useJoinClinicSite from '../../../features/query-hooks/useJoinClinicSite'
import { Site } from '../../../features/query-hooks/useSitesAvailable'
import useSiteSearch from '../../../features/query-hooks/useSiteSearch'
import { useHistory } from '../../../utils/useHistory'
import FormContainer from '../components/FormContainer'
import FormHeader from '../components/FormHeader'

const useStyles = makeStyles((theme) => ({
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: theme.spacing(3),
    maxWidth: '960px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  },
  formHeader: {
    marginBottom: '32px',
  },
  formTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#212121',
    marginBottom: '16px',
  },
  formDescription: {
    fontSize: '14px',
    color: '#212121',
    lineHeight: '20px',
    marginBottom: '32px',
  },
  inputField: {
    marginBottom: '0px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#FAFAFB',
      paddingRight: '9px !important',
      '& fieldset': {
        borderColor: '#E1E4E8',
        '& legend': {
          visibility: 'hidden',
          maxWidth: '0.01px',
        },
      },
      '&:hover fieldset': {
        borderColor: '#C4C9CF',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#212121',
      fontSize: '14px',
      fontWeight: 400,
      marginBottom: '8px',
    },
    // Override the specific Autocomplete padding
    '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
      paddingRight: '9px !important',
    },
    '&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
      paddingRight: '9px !important',
    },
  },
  clinicDropdown: {
    '& .MuiAutocomplete-paper': {
      borderRadius: '8px',
      boxShadow: '0px 4px 8px 0px rgba(0,0,0,0.1)',
      marginTop: '8px',
      maxHeight: '400px',
    },
    '& .MuiAutocomplete-listbox': {
      maxHeight: '300px',
      '& .MuiAutocomplete-option': {
        padding: '6px',
        borderRadius: '2px',
        minHeight: 'auto',
        '&:hover': {
          backgroundColor: 'rgba(43,173,238,0.05)',
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'rgba(43,173,238,0.05)',
        },
      },
    },
  },
  selectedClinicCard: {
    backgroundColor: 'rgba(43,173,238,0.05)',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '32px',
  },
  selectedClinicName: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#212121',
    lineHeight: '24px',
    marginBottom: '4px',
  },
  selectedClinicAddress: {
    fontSize: '12px',
    color: '#212121',
    lineHeight: '16px',
  },
  submitButton: {
    marginTop: '8px',
    width: '100%',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'none',
    backgroundColor: '#F1F1F1',
    color: '#919191',
    '&:hover': {
      backgroundColor: '#F1F1F1',
    },
    '&:disabled': {
      backgroundColor: '#F1F1F1',
      color: '#919191',
    },
  },
  submitButtonEnabled: {
    width: '100%',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'none',
    backgroundColor: theme.palette.primary.dark,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}))

interface SearchExistingClinicData {
  clinicName: string
  selectedClinic?: Site | null
}

const validationSchema = yup.object().shape({
  clinicName: yup.string().required('Clinic name is required'),
})

interface SearchExistingClinicProps {
  onBack?: () => void
  onCreateNew?: () => void
}

const SearchExistingClinic: FC<SearchExistingClinicProps> = ({
  onBack: _onBack,
}) => {
  const classes = useStyles()
  const { user, signOut } = useAuth()
  const { sitesQuery: userSitesQuery } = useSitesContext()
  const queryClient = useQueryClient()
  const { push } = useHistory()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [selectedClinic, setSelectedClinic] = useState<Site | null>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(true)
  const [hasSearched, setHasSearched] = useState(false)

  // Prevent existing users with sites from accessing this page
  if (userSitesQuery.data && userSitesQuery.data.length > 0) {
    return <AnimatedRedirect to="/home" />
  }

  // Fade out success message after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSuccessMessage(false)
    }, 3000)

    return () => clearTimeout(timer)
  }, [])

  // Navigation functions
  const handleSignOut = () => {
    signOut()
  }

  // Hook to join existing clinic (test mutation)
  const joinClinicSite = useJoinClinicSite()

  // Search for clinics (now with location data via :all view)
  const [searchTerm, setSearchTerm] = useState('')
  const sitesQuery = useSiteSearch(
    {
      filter: '',
      search: searchTerm,
      page: 1,
      perPage: 50,
    },
    {
      enabled: searchTerm.length > 2, // Only search when user has typed at least 3 characters
    },
  )

  // Deduplicate sites by ID to avoid duplicate results
  const uniqueSites = sitesQuery.data?.sites
    ? sitesQuery.data.sites.reduce(
        (acc, site) => {
          if (!acc.find((existing) => existing.id === site.id)) {
            acc.push(site)
          }
          return acc
        },
        [] as typeof sitesQuery.data.sites,
      )
    : []

  // Track when user has searched (3+ characters triggers search)
  useEffect(() => {
    if (searchTerm.length > 2) {
      setHasSearched(true)
    }
  }, [searchTerm])

  const formik = useFormik<SearchExistingClinicData>({
    initialValues: {
      clinicName: '',
      selectedClinic: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitError(null)

      try {
        // Get the staff ID from authenticated user
        const staffId = user?.staff?.id

        if (!staffId) {
          throw new Error('User not authenticated. Please log in to continue.')
        }

        if (!values.selectedClinic?.id) {
          throw new Error('Please select a clinic to continue.')
        }

        // Test: Join existing clinic using specific site ID
        await joinClinicSite.mutateAsync({
          staffId: staffId,
          site: values.selectedClinic,
          staff: user.staff,
        })

        // Invalidate current user query to refresh user data after clinic assignment
        await queryClient.invalidateQueries(QueryKeys.currentUser())

        // Small delay to ensure query invalidation completes before redirect
        setTimeout(() => {
          push('/pending-approval')
        }, 100)
      } catch (error) {
        console.error('Clinic join error:', error)

        // Enhanced error logging for debugging
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as {
            response?: { data?: unknown; status?: number }
          }
          console.error('Server response:', axiosError.response?.data)
          console.error('Status code:', axiosError.response?.status)
        }

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to join clinic. Please try again.'
        setSubmitError(errorMessage)
      }
    },
  })

  // Get clinic location string for display
  const getClinicLocationString = (site: Site): string => {
    const primaryLocation =
      site.siteLocations?.find((loc) => loc.isPrimary) ||
      site.siteLocations?.[0]
    if (primaryLocation) {
      return `${primaryLocation.city}, ${primaryLocation.stateProvince}`
    }
    // Fallback for when no location data is available
    return 'Location not specified'
  }

  // Get clinic address for display with fallback for missing data
  const getClinicAddress = (site: Site): string => {
    const primaryLocation =
      site.siteLocations?.find((loc) => loc.isPrimary) ||
      site.siteLocations?.[0]

    if (!primaryLocation) {
      return 'Address not specified'
    }

    // Build address string with available parts
    const addressParts = [
      primaryLocation.street,
      primaryLocation.city,
      primaryLocation.stateProvince,
      primaryLocation.zipCode,
    ].filter((part) => part && part.trim()) // Filter out empty parts

    return addressParts.length > 0
      ? addressParts.join(', ')
      : 'Address not specified'
  }

  return (
    <Box className={classes.formCard}>
      <FormHeader
        title="Set up your clinic"
        description="Now, set up your clinic to start tracking outcomes. Search for your clinic to connect your account — or create a new one if it's not listed."
        rightButton={
          <Button
            variant="text"
            startIcon={<LogOut size={16} />}
            onClick={handleSignOut}
            style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Sign Out
          </Button>
        }
      />

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '16px',
          marginBottom: showSuccessMessage ? '24px' : '0px',
          opacity: showSuccessMessage ? 1 : 0,
          transition: 'opacity 0.5s ease-out, margin-bottom 0.5s ease-out',
          height: showSuccessMessage ? 'auto' : 0,
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: '#00A800',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Check size={16} color="#FFFFFF" />
        </Box>
        <Typography
          style={{
            fontSize: '14px',
            color: '#212121',
            fontWeight: 700,
          }}
        >
          Your provider account has been created successfully.
        </Typography>
      </Box>

      {submitError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {submitError}
        </Alert>
      )}

      <FormContainer>
        <form onSubmit={formik.handleSubmit}>
          <Autocomplete
            options={uniqueSites}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedClinic}
            onChange={(_, newValue) => {
              const selectedSite = newValue as Site | null
              setSelectedClinic(selectedSite)
              formik.setFieldValue('clinicName', selectedSite?.name || '')
              formik.setFieldValue('selectedClinic', selectedSite)
            }}
            inputValue={formik.values.clinicName}
            onInputChange={(_, newInputValue) => {
              formik.setFieldValue('clinicName', newInputValue)
              setSearchTerm(newInputValue)
              if (!newInputValue) {
                setSelectedClinic(null)
                setSearchTerm('')
                formik.setFieldValue('selectedClinic', null)
              }
            }}
            noOptionsText={
              searchTerm.length <= 2
                ? 'Start typing to search...'
                : 'No clinics found'
            }
            className={classes.clinicDropdown}
            renderOption={(props, option) => {
              const location = getClinicLocationString(option)
              return (
                <li
                  {...props}
                  style={{
                    padding: '12px 6px 12px 16px',
                    borderRadius: '2px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#000000',
                        lineHeight: '16px',
                      }}
                    >
                      {option.name}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#767676',
                        lineHeight: '12px',
                        fontWeight: 400,
                      }}
                    >
                      {location}
                    </div>
                  </div>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="clinicName"
                label="Clinic Name"
                variant="outlined"
                fullWidth
                className={classes.inputField}
                error={
                  formik.touched.clinicName && Boolean(formik.errors.clinicName)
                }
                helperText={
                  formik.touched.clinicName && formik.errors.clinicName
                    ? formik.errors.clinicName
                    : "Search for your clinic to verify it isn't already registered before creating a new one"
                }
                disabled={formik.isSubmitting}
                placeholder="Start typing in your clinic's name"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#919191" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Selected Clinic Info Card */}
          {selectedClinic && (
            <Box className={classes.selectedClinicCard}>
              <Typography className={classes.selectedClinicName}>
                {selectedClinic.name}
              </Typography>
              <Typography className={classes.selectedClinicAddress}>
                {getClinicAddress(selectedClinic)}
              </Typography>
            </Box>
          )}

          <Button
            type={selectedClinic ? 'submit' : 'button'}
            variant="contained"
            className={
              selectedClinic || hasSearched
                ? classes.submitButtonEnabled
                : classes.submitButton
            }
            disabled={
              !hasSearched || formik.isSubmitting || joinClinicSite.isLoading
            }
            onClick={() => {
              if (!selectedClinic && hasSearched) {
                // Navigate to create clinic page
                localStorage.setItem('isCreatingClinic', 'true')
                push('/provider/create-clinic')
              }
              // If selectedClinic exists, form submission will handle it
            }}
            startIcon={selectedClinic ? undefined : <Plus size={16} />}
            style={{
              borderRadius: '8px',
            }}
          >
            {selectedClinic
              ? `Join ${selectedClinic.name}`
              : 'Create New Clinic'}
          </Button>
        </form>
      </FormContainer>
    </Box>
  )
}

export default SearchExistingClinic
